import { AskTwinLink } from "@/components/app/sessions/ask-twin";
import type { ComponentProps } from "react";

import {
  ChannelAddMenuTrigger,
  ChannelMemoryButton,
} from "@product/app/workspace/channels/[channelId]/channel-header-actions-view";
import { ChannelTabColumn } from "@product/app/workspace/channels/[channelId]/channel-tab-column";
import {
  ChannelAccessButton,
  ChannelWorkspaceToolbarEndView,
  ChannelWorkspaceToolbarView,
} from "@product/app/workspace/channels/[channelId]/channel-workspace-toolbar-view";
import { buildNewSessionHref } from "@product/lib/new-session-route";

import type { AppThemeTone } from "@/components/editorial/product-bridge/app-surface";

import { ShotChannelFeedView } from "./channel-feed-view";
import { ShotRail } from "./chrome";
import type { ShotChannelFeed } from "./fixture-engineering";
import { SHOT_WINDOW_HEIGHT_CLASS, ShotAppFrame } from "./window";
import type { ShotWorkspace } from "./workspace";

/**
 * A channel on its Feed tab, in the application frame: the channel's title
 * row, its section tabs on Feed, then the feed in the column every channel
 * tab reads in (ChannelFeedPage). The wrappers between them are the channel
 * surface's (channel-workspace-surface.tsx), as the channel shot's are
 * (channel-window.tsx); everything inside is the product's own views over
 * the feed's posts.
 */
export function ShotChannelFeedWindow({
  tone = "light",
  workspace,
  feed,
  className,
  heightClass = SHOT_WINDOW_HEIGHT_CLASS,
  ...props
}: {
  tone?: AppThemeTone;
  workspace: ShotWorkspace;
  feed: ShotChannelFeed;
  className?: string;
  heightClass?: string;
} & Omit<ComponentProps<"div">, "children">) {
  const { channel } = feed;
  const channelHref = `/workspace/channels/${encodeURIComponent(channel.id)}`;
  const newSessionHref = buildNewSessionHref({ channelId: channel.id, returnTo: channelHref });
  return (
    <ShotAppFrame
      tone={tone}
      workspace={workspace}
      rail={<ShotRail workspace={workspace} tab="channels" activeChannelId={channel.id} />}
      className={className}
      heightClass={heightClass}
      {...props}
    >
      {/* The workspace shell's page content (workspace-navigation-shell). */}
      <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <section className="channel-workspace-container flex h-full min-h-0 flex-col bg-background [--channel-canvas:hsl(var(--background))]">
          <ChannelWorkspaceToolbarView
            channel={channel}
            // The channel's Activity page, as its title row names it.
            crumb={{ name: "Activity" }}
            end={
              <ChannelWorkspaceToolbarEndView
                actions={
                  <>
                    <ChannelAddMenuTrigger />
                    <AskTwinLink href={newSessionHref} title="New session in this channel" />
                    <ChannelMemoryButton href={`/workspace/channels/${channel.id}?view=memory`} />
                    <div className="size-7 shrink-0">
                      <ChannelAccessButton />
                    </div>
                  </>
                }
              />
            }
          />
          <div className="flex h-full min-h-0 w-full flex-1">
            <div className="min-w-0 flex-1 overflow-hidden">
              <div className="relative h-full min-h-0">
                <div className="flex h-full min-h-0 flex-col overflow-hidden">
                  <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                    <ChannelTabColumn fill>
                      <ShotChannelFeedView feed={feed} />
                    </ChannelTabColumn>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </ShotAppFrame>
  );
}
